import React, { useState, useEffect } from "react";
import { Logo } from "./Logo";
import { useReveal } from "../hooks/useReveal";
import FeatureShowcase from "./FeatureShowcase";
import ParksCarousel from "./ParksCarousel";
import DeepDive from "./DeepDive";
import naturtrakLogo from "@/assets/naturtraklogo.png";
import termvedLogo from "@/assets/termvedlogo.png";
import athenaLogo from "@/assets/athena_logo.svg";
import athenaScreenshot from "@/assets/athena_screenshot.png";
import { t } from "@/i18n/translations";
import {
  Map,
  Camera,
  Bird,
  Sparkles,
  Route as RouteIcon,
  Stamp,
  ArrowRight,
  Github,
  Mail,
  ExternalLink,
  Mountain,
  Smartphone,
  Server,
  Cloud,
  Database,
  ChevronRight,
  BookOpen,
  GraduationCap,
  BarChart3,
  Play,
  ShieldCheck,
  Recycle,
  Sprout,
  Vote,
} from "lucide-react";

const Nav = ({ lang, onToggleLang }) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { href: "#features", label: t(lang, "nav.features") },
    { href: "#athena", label: t(lang, "nav.athena") },
    { href: "#ecosystem", label: t(lang, "nav.ecosystem") },
    { href: "#contact", label: t(lang, "nav.contact") },
  ];

  return (
    <header
      data-testid="nt-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#1E1E1E]/85 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
        <a
          href="#top"
          className="flex items-center gap-3 group"
          data-testid="nav-logo-link"
        >
          <img src={naturtrakLogo} alt="NaturTrak" className="h-8 w-auto object-contain" />
          <span className="text-xl font-bold tracking-tight text-[#FDF5EB]">
            Natur<span className="text-[#FBD556]">Trak</span>
          </span>
        </a>

        <nav
          className="hidden md:flex items-center gap-8"
          aria-label="Primary navigation"
        >
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-medium text-[#FDF5EB]/70 hover:text-[#FBD556] transition-colors"
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label="Toggle language"
            data-testid="lang-toggle"
            onClick={onToggleLang}
            className="hidden sm:inline-flex text-xs font-semibold text-[#FDF5EB]/60 border border-white/10 rounded-full px-3 py-1.5 hover:border-[#FBD556]/50 hover:text-[#FDF5EB] transition-colors"
          >
            {lang === "en" ? (
              <>
                HU / <span className="text-[#FBD556] ml-1">EN</span>
              </>
            ) : (
              <>
                <span className="text-[#FBD556] mr-1">HU</span> / EN
              </>
            )}
          </button>
          <a
            href="#contact"
            data-testid="cta-request-demo"
            className="inline-flex items-center gap-2 bg-[#FBD556] text-[#242220] font-bold text-sm px-5 py-2.5 rounded-full hover:bg-[#FFE07A] transition-all hover:shadow-[0_0_24px_rgba(251,213,86,0.35)]"
          >
            {t(lang, "nav.requestDemo")}
            <ArrowRight size={16} strokeWidth={2.5} />
          </a>
        </div>
      </div>
    </header>
  );
};

const Hero = ({ lang }) => {
  return (
    <section
      id="top"
      data-testid="section-hero"
      className="topo-bg grain relative min-h-screen flex items-center pt-32 pb-20"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-[1.2fr_1fr] gap-12 lg:gap-20 items-center relative z-10 w-full">
        <div>
          <div
            className="reveal inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#A1E1B9]/30 bg-[#A1E1B9]/5 mb-8"
            data-testid="hero-eyebrow"
          >
            <span className="w-2 h-2 rounded-full bg-[#A1E1B9] animate-pulse" />
            <span className="text-xs font-semibold tracking-wider uppercase text-[#A1E1B9]">
              {t(lang, "hero.eyebrow")}
            </span>
          </div>

          <h1
            className="reveal reveal-delay-1 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-[#FDF5EB] mb-6"
            data-testid="hero-headline"
          >
            {t(lang, "hero.headline")}
            <br />
            {lang === "hu" ? " " : "for "}
            <span className="relative inline-block">
              <span className="text-[#FBD556]">{t(lang, "hero.headlineHighlight")}</span>
              <svg
                className="absolute -bottom-2 left-0 w-full"
                height="10"
                viewBox="0 0 320 10"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path
                  d="M2 7 Q 80 1, 160 5 T 318 4"
                  stroke="#FF9456"
                  strokeWidth="2.5"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            {lang === "hu" && (
              <span className="hero-float-chip" data-testid="hero-float-chip">
                {t(lang, "hero.headlineFloat")}
              </span>
            )}
          </h1>

          <p
            className="reveal reveal-delay-2 text-base sm:text-lg text-[#FDF5EB]/70 max-w-xl mb-10 leading-relaxed"
            data-testid="hero-subhead"
          >
            {t(lang, "hero.subhead")}
          </p>

          <div
            className="reveal reveal-delay-3 flex flex-wrap items-center gap-4"
            data-testid="hero-ctas"
          >
            <a
              href="#features"
              data-testid="hero-cta-primary"
              className="inline-flex items-center gap-2 bg-[#FBD556] text-[#242220] font-bold text-sm px-6 py-3.5 rounded-full hover:bg-[#FFE07A] transition-all hover:shadow-[0_0_30px_rgba(251,213,86,0.4)]"
            >
              {t(lang, "hero.exploreFeatures")}
              <ChevronRight size={18} strokeWidth={2.5} />
            </a>
            <a
              href="https://github.com/balakhorvathnorbert"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 text-[#FDF5EB] font-semibold text-sm px-6 py-3.5 rounded-full border border-[#FDF5EB]/20 hover:border-[#FBD556]/60 hover:bg-white/5 transition-all"
            >
              <Github size={18} />
              {t(lang, "hero.viewOnGitHub")}
            </a>
          </div>

          <div
            className="reveal reveal-delay-4 mt-12 flex items-center gap-6 text-xs text-[#FDF5EB]/40"
            data-testid="hero-trust"
          >
            <span className="flex items-center gap-2">
              <Mountain size={14} className="text-[#A1E1B9]" />
              {t(lang, "hero.builtByRanger")}
            </span>
            <span className="hidden sm:inline w-px h-4 bg-white/10" />
            <span className="hidden sm:inline">{t(lang, "hero.techStack")}</span>
          </div>
        </div>

        <div className="relative flex justify-center lg:justify-end">
          <div className="float-device relative">
            <div className="absolute -inset-10 bg-[#A1E1B9] opacity-20 blur-3xl rounded-full" />
            <div
              className="relative w-[260px] h-[520px] rounded-[44px] border-[10px] border-[#242220] bg-[#0f0f0f] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8),0_0_0_2px_rgba(251,213,86,0.15)] overflow-hidden"
              data-testid="hero-phone-mockup"
            >
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-[#242220] rounded-b-2xl z-20" />
              <div className="absolute inset-0 bg-gradient-to-br from-[#1a2a1f] via-[#1E1E1E] to-[#2a1f1a]">
                <svg
                  className="absolute inset-0 w-full h-full"
                  viewBox="0 0 260 520"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <g
                    fill="none"
                    stroke="#A1E1B9"
                    strokeWidth="0.6"
                    opacity="0.4"
                  >
                    <path d="M-10,120 Q60,90 130,110 T280,140" />
                    <path d="M-10,160 Q60,130 130,150 T280,180" />
                    <path d="M-10,200 Q60,170 130,190 T280,220" />
                    <path d="M-10,260 Q60,240 130,250 T280,280" />
                    <path d="M-10,310 Q60,290 130,300 T280,330" />
                    <path d="M-10,380 Q60,360 130,370 T280,400" />
                    <path d="M-10,430 Q60,410 130,420 T280,450" />
                  </g>
                  <path
                    d="M40,420 Q90,360 110,300 T160,200 Q180,150 200,100"
                    stroke="#FBD556"
                    strokeWidth="2.5"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray="0"
                  />
                  <circle cx="40" cy="420" r="6" fill="#FF9456" />
                  <circle cx="40" cy="420" r="3" fill="#242220" />
                  <circle cx="200" cy="100" r="6" fill="#FBD556" />
                  <circle cx="200" cy="100" r="3" fill="#242220" />
                </svg>

                <div className="absolute top-10 left-0 right-0 px-5 flex items-center justify-between text-[10px] text-[#FDF5EB]/70 font-semibold">
                  <span>9:41</span>
                  <span className="px-2 py-0.5 rounded-full bg-[#A1E1B9]/20 text-[#A1E1B9]">
                    {t(lang, "hero.patrolActive")}
                  </span>
                </div>

                <div className="absolute bottom-4 left-3 right-3 bg-[#1E1E1E]/90 backdrop-blur-md rounded-2xl p-3.5 border border-white/10">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-[#FBD556]/20 flex items-center justify-center">
                      <Bird size={14} className="text-[#FBD556]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-bold text-[#FDF5EB] truncate">
                        Aquila heliaca
                      </div>
                      <div className="text-[9px] text-[#FDF5EB]/50">
                        {t(lang, "hero.eagleSpecies")}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5 text-center">
                    <div className="bg-white/5 rounded-md py-1.5">
                      <div className="text-[10px] font-bold text-[#FBD556]">
                        4.2 km
                      </div>
                      <div className="text-[8px] text-[#FDF5EB]/40">
                        {t(lang, "hero.distance")}
                      </div>
                    </div>
                    <div className="bg-white/5 rounded-md py-1.5">
                      <div className="text-[10px] font-bold text-[#A1E1B9]">
                        01:24
                      </div>
                      <div className="text-[8px] text-[#FDF5EB]/40">{t(lang, "hero.time")}</div>
                    </div>
                    <div className="bg-white/5 rounded-md py-1.5">
                      <div className="text-[10px] font-bold text-[#FF9456]">
                        7
                      </div>
                      <div className="text-[8px] text-[#FDF5EB]/40">
                        {t(lang, "hero.sightings")}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const StatsBar = ({ lang }) => {
  const stats = [
    { num: "10", label: t(lang, "stats.parks") },
    { num: "21", label: t(lang, "stats.methods") },
    { num: "4", label: t(lang, "stats.aiModels") },
    { num: "∞", label: t(lang, "stats.routing") },
    { num: "75", label: t(lang, "stats.modules") },
    { num: "147", label: t(lang, "stats.rangers") },
  ];

  return (
    <section
      data-testid="section-stats"
      className="border-y border-white/5 bg-[#171717]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
        {stats.map((s, i) => (
          <div
            key={s.label}
            className={`reveal reveal-delay-${i + 1} text-center lg:text-left`}
            data-testid={`stat-${i}`}
          >
            <div className="text-4xl lg:text-5xl font-bold text-[#FBD556] mb-2 tracking-tight">
              {s.num}
            </div>
            <div className="text-xs lg:text-sm text-[#FDF5EB]/60 leading-snug">
              {s.label}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const SceneFrame = ({ children, grid = true }) => (
  <svg
    viewBox="0 0 260 128"
    className="w-full h-full"
    preserveAspectRatio="xMidYMid slice"
    aria-hidden="true"
  >
    {grid && (
      <g stroke="#FDF5EB" strokeOpacity="0.06" strokeWidth="1">
        {Array.from({ length: 9 }).map((_, i) => (
          <React.Fragment key={i}>
            <line x1={i * 30} y1="0" x2={i * 30} y2="128" />
            <line x1="0" y1={i * 16} x2="260" y2={i * 16} />
          </React.Fragment>
        ))}
      </g>
    )}
    {children}
  </svg>
);

const SpatialAnalyticsScene = ({ lang }) => {
  const s = t(lang, "features.scenes.spatial");
  return (
    <SceneFrame>
      <path
        d="M24 90 L 70 64 L 118 86 L 92 118 L 34 118 Z"
        fill="#A1E1B9"
        fillOpacity="0.14"
        stroke="#A1E1B9"
        strokeOpacity="0.55"
        strokeWidth="1.5"
      />
      <path
        d="M132 52 L 190 38 L 228 76 L 190 102 L 146 90 Z"
        fill="#FBD556"
        fillOpacity="0.12"
        stroke="#FBD556"
        strokeOpacity="0.55"
        strokeWidth="1.5"
      />
      <path
        d="M56 34 Q 104 14, 148 40 T 248 60"
        stroke="#FF9456"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
        strokeDasharray="5 4"
      />
      <circle cx="56" cy="34" r="4.5" fill="#FF9456" />
      <circle cx="248" cy="60" r="4.5" fill="#FBD556" />
      <g transform="translate(12 12)">
        <rect width="92" height="20" rx="10" fill="#242220" />
        <text x="10" y="14" fontSize="9" fontWeight="700" fontFamily="Quicksand" fill="#A1E1B9">
          {s.badge}
        </text>
      </g>
      <text x="14" y="122" fontSize="8" fontFamily="Quicksand" fill="#FDF5EB" opacity="0.6">
        {s.caption}
      </text>
    </SceneFrame>
  );
};

const SpeciesScene = ({ lang }) => {
  const s = t(lang, "features.scenes.species");
  return (
    <SceneFrame>
      <rect x="14" y="16" width="150" height="88" rx="10" fill="#242220" stroke="#FDF5EB" strokeOpacity="0.15" strokeWidth="1" />
      <path d="M36 86 Q 52 48, 84 60 T 148 78" stroke="#FBD556" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M84 60 q 16 -10 34 -4 q -18 4 -34 8" fill="#FBD556" />
      <circle cx="148" cy="78" r="2.5" fill="#FBD556" />
      <g transform="translate(176 16)">
        <rect width="70" height="20" rx="10" fill="#FBD556" />
        <text x="35" y="14" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#242220">
          {s.badge}
        </text>
      </g>
      {[
        { x: 96, y: 26 },
        { x: 140, y: 44 },
        { x: 62, y: 24 },
      ].map((sp, i) => (
        <path
          key={i}
          d={`M${sp.x} ${sp.y - 5} L${sp.x + 2} ${sp.y - 2} L${sp.x + 5} ${sp.y} L${sp.x + 2} ${sp.y + 2} L${sp.x} ${sp.y + 5} L${sp.x - 2} ${sp.y + 2} L${sp.x - 5} ${sp.y} L${sp.x - 2} ${sp.y - 2} Z`}
          fill="#A1E1B9"
        />
      ))}
      <text x="26" y="122" fontSize="8" fontFamily="Quicksand" fill="#FDF5EB" opacity="0.6">
        {s.caption}
      </text>
    </SceneFrame>
  );
};

const BirdScene = ({ lang }) => {
  const s = t(lang, "features.scenes.bird");
  return (
    <SceneFrame>
      <rect x="14" y="18" width="150" height="56" rx="10" fill="#242220" stroke="#A1E1B9" strokeOpacity="0.4" strokeWidth="1" />
      {[6, 10, 14, 18, 12, 16, 8, 14, 18, 10].map((h, i) => (
        <rect key={i} x={24 + i * 14} y={64 - h} width="7" height={h} rx="2" fill={i % 3 === 1 ? "#FF9456" : "#A1E1B9"} fillOpacity={i % 3 === 1 ? 0.9 : 0.55} />
      ))}
      <circle cx="196" cy="46" r="14" fill="#A1E1B9" fillOpacity="0.15" stroke="#A1E1B9" strokeOpacity="0.5" />
      <path d="M190 46 l 5 4 l 9 -9" stroke="#A1E1B9" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <g transform="translate(176 84)">
        <rect width="70" height="20" rx="10" fill="#242220" stroke="#A1E1B9" strokeOpacity="0.4" strokeWidth="1" />
        <text x="35" y="14" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#A1E1B9">
          {s.badge}
        </text>
      </g>
      <text x="14" y="116" fontSize="8" fontFamily="Quicksand" fill="#FDF5EB" opacity="0.6">
        {s.caption}
      </text>
    </SceneFrame>
  );
};

const CameraScene = ({ lang }) => {
  const s = t(lang, "features.scenes.camera");
  return (
    <SceneFrame>
      <g transform="translate(24 40)">
        <rect width="96" height="60" rx="12" fill="#242220" stroke="#FBD556" strokeOpacity="0.5" strokeWidth="1.5" />
        <path d="M24 12 L 36 4 L 48 12" fill="#242220" stroke="#FBD556" strokeOpacity="0.5" strokeWidth="1.5" />
        <circle cx="60" cy="40" r="16" fill="#1E1E1E" stroke="#A1E1B9" strokeWidth="2" />
        <circle cx="60" cy="40" r="7" fill="#A1E1B9" fillOpacity="0.4" />
      </g>
      <path d="M140 62 q 10 -7 0 -14" stroke="#A1E1B9" strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.7" />
      <path d="M154 62 q 10 -7 0 -14" stroke="#A1E1B9" strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.7" />
      <g transform="translate(176 34)">
        <rect width="70" height="20" rx="10" fill="#A1E1B9" />
        <text x="35" y="14" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#242220">
          {s.badge}
        </text>
      </g>
      <text x="14" y="118" fontSize="8" fontFamily="Quicksand" fill="#FDF5EB" opacity="0.6">
        {s.caption}
      </text>
    </SceneFrame>
  );
};

const RouteScene = ({ lang }) => {
  const s = t(lang, "features.scenes.routing");
  return (
    <SceneFrame>
      <path d="M40 96 Q 90 74, 130 88 T 236 44" stroke="#A1E1B9" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <circle cx="40" cy="96" r="5" fill="#FBD556" />
      <circle cx="236" cy="44" r="5" fill="#FF9456" />
      <g transform="translate(12 12)">
        <rect width="92" height="20" rx="10" fill="#242220" stroke="#FBD556" strokeOpacity="0.4" strokeWidth="1" />
        <text x="46" y="14" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#FBD556">
          {s.ranger}
        </text>
      </g>
      <g transform="translate(156 88)">
        <rect width="92" height="20" rx="10" fill="#242220" stroke="#A1E1B9" strokeOpacity="0.4" strokeWidth="1" />
        <text x="46" y="14" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#A1E1B9">
          {s.hiker}
        </text>
      </g>
      <text x="14" y="122" fontSize="8" fontFamily="Quicksand" fill="#FDF5EB" opacity="0.6">
        {s.caption}
      </text>
    </SceneFrame>
  );
};

const PassScene = ({ lang }) => {
  const s = t(lang, "features.scenes.pass");
  return (
    <SceneFrame>
      <rect x="14" y="20" width="228" height="88" rx="10" fill="#242220" stroke="#FBD556" strokeOpacity="0.4" strokeWidth="1.5" />
      <rect x="26" y="32" width="64" height="64" rx="6" fill="#FDF5EB" stroke="#242220" strokeWidth="1" />
      <circle cx="58" cy="56" r="14" fill="none" stroke="#FF9456" strokeWidth="2" />
      <text x="58" y="60" textAnchor="middle" fontSize="7" fontWeight="700" fontFamily="Quicksand" fill="#FF9456">
        STAMP
      </text>
      <rect x="104" y="32" width="64" height="64" rx="6" fill="#FDF5EB" stroke="#242220" strokeWidth="1" />
      <g fill="#242220">
        <rect x="118" y="46" width="8" height="8" />
        <rect x="130" y="46" width="8" height="8" />
        <rect x="146" y="46" width="8" height="8" />
        <rect x="118" y="58" width="8" height="8" />
        <rect x="130" y="58" width="8" height="8" />
        <rect x="146" y="66" width="8" height="8" />
        <rect x="118" y="74" width="8" height="8" />
        <rect x="138" y="74" width="8" height="8" />
      </g>
      <g transform="translate(182 32)">
        <rect width="46" height="64" rx="6" fill="#A1E1B9" stroke="#242220" strokeWidth="1" />
        <text x="23" y="42" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#242220">
          {s.gps}
        </text>
        <text x="23" y="54" textAnchor="middle" fontSize="8" fontWeight="700" fontFamily="Quicksand" fill="#242220">
          {s.check}
        </text>
      </g>
      <text x="14" y="122" fontSize="8" fontFamily="Quicksand" fill="#FDF5EB" opacity="0.6">
        {s.caption}
      </text>
    </SceneFrame>
  );
};

const Features = ({ lang }) => {
  const featureCards = t(lang, "features.cards");

  const icons = [Map, Sparkles, Bird, Camera, RouteIcon, Stamp];
  const scenes = [SpatialAnalyticsScene, SpeciesScene, BirdScene, CameraScene, RouteScene, PassScene];
  const accents = ["#FBD556", "#A1E1B9", "#FF9456", "#FBD556", "#A1E1B9", "#FF9456"];

  return (
    <section
      id="features"
      data-testid="section-features"
      className="py-24 lg:py-32 relative"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl mb-16">
          <div
            className="reveal inline-flex items-center gap-2 mb-4"
            data-testid="features-eyebrow"
          >
            <span className="h-px w-8 bg-[#FBD556]" />
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#FBD556]">
              {t(lang, "features.eyebrow")}
            </span>
          </div>
          <h2
            className="reveal reveal-delay-1 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#FDF5EB] tracking-tight leading-tight"
            data-testid="features-heading"
          >
            {t(lang, "features.heading")}
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {featureCards.map((f, i) => {
            const Icon = icons[i];
            const Scene = scenes[i];
            return (
              <div
                key={f.title}
                data-testid={`feature-card-${i}`}
                className={`feature-card reveal reveal-delay-${(i % 3) + 1} relative p-7 rounded-2xl border border-white/8 bg-white/[0.02] group`}
              >
                <div className="relative mb-5">
                  <div className="h-32 rounded-xl overflow-hidden border border-white/10 bg-[#141414]">
                    <Scene lang={lang} />
                  </div>
                  <div
                    className="absolute top-3 left-3 w-10 h-10 rounded-lg flex items-center justify-center border border-white/10"
                    style={{ backgroundColor: `${accents[i]}26` }}
                  >
                    <Icon
                      size={18}
                      style={{ color: accents[i] }}
                      strokeWidth={2.2}
                    />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-[#FDF5EB] mb-3 tracking-tight">
                  {f.title}
                </h3>
                <p className="text-sm text-[#FDF5EB]/60 leading-relaxed">
                  {f.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Ecosystem = ({ lang }) => {
  const flowNodes = [
    { icon: Smartphone, label: t(lang, "ecosystem.flow.0.label"), sub: t(lang, "ecosystem.flow.0.sub"), color: "#FBD556" },
    { icon: Server, label: t(lang, "ecosystem.flow.1.label"), sub: t(lang, "ecosystem.flow.1.sub"), color: "#A1E1B9" },
    { icon: Database, label: t(lang, "ecosystem.flow.2.label"), sub: t(lang, "ecosystem.flow.2.sub"), color: "#FF9456" },
    { icon: Cloud, label: t(lang, "ecosystem.flow.3.label"), sub: t(lang, "ecosystem.flow.3.sub"), color: "#FBD556" },
  ];

  const columns = [
    { title: t(lang, "ecosystem.columns.0.title"), color: "#FBD556", items: t(lang, "ecosystem.columns.0.items") },
    { title: t(lang, "ecosystem.columns.1.title"), color: "#A1E1B9", items: t(lang, "ecosystem.columns.1.items") },
    { title: t(lang, "ecosystem.columns.2.title"), color: "#FF9456", items: t(lang, "ecosystem.columns.2.items") },
    { title: t(lang, "ecosystem.columns.3.title"), color: "#A1E1B9", items: t(lang, "ecosystem.columns.3.items") },
  ];

  return (
    <section
      id="ecosystem"
      data-testid="section-ecosystem"
      className="py-24 lg:py-32 bg-[#171717] border-y border-white/5 relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl mb-16">
          <div className="reveal inline-flex items-center gap-2 mb-4">
            <span className="h-px w-8 bg-[#A1E1B9]" />
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#A1E1B9]">
              {t(lang, "ecosystem.eyebrow")}
            </span>
          </div>
          <h2
            className="reveal reveal-delay-1 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#FDF5EB] tracking-tight leading-tight"
            data-testid="ecosystem-heading"
          >
            {t(lang, "ecosystem.heading")}
          </h2>
        </div>

        <div
          className="reveal reveal-delay-2 mb-16 overflow-x-auto pb-4"
          data-testid="ecosystem-flow"
        >
          <div className="min-w-[760px] flex items-center justify-between gap-3 p-6 rounded-2xl border border-white/10 bg-[#1E1E1E]/60">
            {flowNodes.map((node, i, arr) => {
              const Icon = node.icon;
              return (
                <React.Fragment key={node.label}>
                  <div className="flex flex-col items-center text-center flex-1 min-w-[140px]">
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center border mb-3"
                      style={{
                        borderColor: `${node.color}55`,
                        backgroundColor: `${node.color}12`,
                      }}
                    >
                      <Icon size={26} style={{ color: node.color }} strokeWidth={1.8} />
                    </div>
                    <div className="text-sm font-bold text-[#FDF5EB] mb-1">
                      {node.label}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-[#FDF5EB]/40">
                      {node.sub}
                    </div>
                  </div>
                  {i < arr.length - 1 && (
                    <div className="flex items-center text-[#FDF5EB]/30 px-1">
                      <svg width="44" height="20" viewBox="0 0 44 20" fill="none">
                        <path
                          d="M2 10 H38 M32 4 L40 10 L32 16"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {columns.map((col, i) => (
            <div
              key={col.title}
              data-testid={`stack-col-${i}`}
              className={`reveal reveal-delay-${i + 1} p-7 rounded-2xl border border-white/8 bg-[#1E1E1E]/40`}
            >
              <div className="flex items-center gap-2 mb-5">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: col.color }}
                />
                <h3
                  className="text-xs font-bold tracking-[0.2em] uppercase"
                  style={{ color: col.color }}
                >
                  {col.title}
                </h3>
              </div>
              <ul className="space-y-3">
                {col.items.map((it) => (
                  <li
                    key={it}
                    className="text-sm text-[#FDF5EB]/75 font-medium flex items-center gap-2"
                  >
                    <span className="text-[#FDF5EB]/30 text-xs">▸</span>
                    {it}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const AthenaSection = ({ lang }) => {
  const athenaFeatures = t(lang, "athena.features");
  const featureIcons = [BookOpen, GraduationCap, BarChart3, Play];

  return (
    <section
      id="athena"
      data-testid="section-athena"
      className="py-24 lg:py-32 bg-[#171717] border-y border-white/5 relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="reveal inline-flex items-center gap-2 mb-4">
          <span className="h-px w-8 bg-[#FBD556]" />
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#FBD556]">
            {t(lang, "athena.eyebrow")}
          </span>
        </div>

        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-16 items-center">
          <div>
            <div className="reveal reveal-delay-1 flex items-center gap-4 mb-6">
              <img
                src={athenaLogo}
                alt="NaturTrak Athena"
                className="h-16 w-auto object-contain"
              />
            </div>

            <h2
              className="reveal reveal-delay-1 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#FDF5EB] tracking-tight leading-tight mb-6"
              data-testid="athena-heading"
            >
              {t(lang, "athena.heading")}{" "}
              <span className="text-[#FBD556]">{t(lang, "athena.accent")}</span>
            </h2>

            <p
              className="reveal reveal-delay-2 text-base sm:text-lg text-[#FDF5EB]/70 max-w-xl mb-8 leading-relaxed"
              data-testid="athena-subhead"
            >
              {t(lang, "athena.subhead")}
            </p>

            <div className="reveal reveal-delay-3 grid sm:grid-cols-2 gap-4 mb-8">
              {athenaFeatures.map((item, i) => {
                const Icon = featureIcons[i];
                return (
                  <div
                    key={item.title}
                    className="flex items-start gap-3 p-4 rounded-xl border border-white/8 bg-white/[0.02]"
                  >
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#FBD556]/10">
                      <Icon size={18} className="text-[#FBD556]" />
                    </div>
                    <div>
                      <div className="text-sm font-bold text-[#FDF5EB] mb-0.5">
                        {item.title}
                      </div>
                      <div className="text-xs text-[#FDF5EB]/50 leading-snug">
                        {item.desc}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="reveal reveal-delay-4 flex flex-wrap items-center gap-4">
              <a
                href="https://athena.naturtrak.eu"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="athena-cta-primary"
                className="inline-flex items-center gap-2 bg-[#FBD556] text-[#242220] font-bold text-sm px-6 py-3.5 rounded-full hover:bg-[#FFE07A] transition-all hover:shadow-[0_0_30px_rgba(251,213,86,0.4)]"
              >
                {t(lang, "athena.openAthena")}
                <ExternalLink size={16} strokeWidth={2.5} />
              </a>
              <a
                href="#features"
                data-testid="athena-cta-secondary"
                className="inline-flex items-center gap-2 text-[#FDF5EB] font-semibold text-sm px-6 py-3.5 rounded-full border border-[#FDF5EB]/20 hover:border-[#A1E1B9]/60 hover:bg-white/5 transition-all"
              >
                {t(lang, "athena.learnMore")}
                <ChevronRight size={16} strokeWidth={2.5} />
              </a>
            </div>
          </div>

          <div className="reveal reveal-delay-2 relative">
            <div className="absolute -inset-8 bg-[#FBD556] opacity-10 blur-3xl rounded-full" />
            <div className="relative rounded-2xl border border-white/10 bg-[#1E1E1E]/80 backdrop-blur-sm overflow-hidden shadow-2xl">
              <img
                src={athenaScreenshot}
                alt="NaturTrak Athena LMS Dashboard"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const HowItWorks = ({ lang }) => {
  const steps = t(lang, "howItWorks.steps");
  const accents = ["#FBD556", "#A1E1B9", "#FF9456"];

  return (
    <section
      id="how-it-works"
      data-testid="section-how"
      className="py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl mb-16">
          <div className="reveal inline-flex items-center gap-2 mb-4">
            <span className="h-px w-8 bg-[#FF9456]" />
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#FF9456]">
              {t(lang, "howItWorks.eyebrow")}
            </span>
          </div>
          <h2
            className="reveal reveal-delay-1 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#FDF5EB] tracking-tight leading-tight"
            data-testid="how-heading"
          >
            {t(lang, "howItWorks.heading")}
          </h2>
        </div>

        <div className="relative grid md:grid-cols-3 gap-6 lg:gap-10">
          <div
            aria-hidden="true"
            className="hidden md:block absolute top-10 left-[12%] right-[12%] h-px border-t border-dashed border-white/15"
          />
          {steps.map((s, i) => (
            <div
              key={s.n}
              data-testid={`step-${i}`}
              className={`reveal reveal-delay-${i + 1} relative bg-[#1E1E1E] rounded-2xl p-8 border border-white/8`}
            >
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mb-6 border-2 font-bold text-2xl"
                style={{
                  borderColor: accents[i],
                  color: accents[i],
                  backgroundColor: `${accents[i]}10`,
                }}
              >
                {s.n}
              </div>
              <h3 className="text-xl font-bold text-[#FDF5EB] mb-3 tracking-tight">
                {s.title}
              </h3>
              <p className="text-sm text-[#FDF5EB]/65 leading-relaxed">
                {s.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const DigitalServices = ({ lang }) => {
  const cards = t(lang, "digitalServices.cards");
  const icons = [ShieldCheck, Recycle, Sprout, Vote];
  const accents = ["#FBD556", "#A1E1B9", "#FF9456", "#FBD556"];

  return (
    <section
      id="digital-services"
      data-testid="section-digital-services"
      className="py-24 lg:py-32 relative"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl mb-16">
          <div className="reveal inline-flex items-center gap-2 mb-4">
            <span className="h-px w-8 bg-[#A1E1B9]" />
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#A1E1B9]">
              {t(lang, "digitalServices.eyebrow")}
            </span>
          </div>
          <h2
            className="reveal reveal-delay-1 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#FDF5EB] tracking-tight leading-tight"
            data-testid="digital-services-heading"
          >
            {t(lang, "digitalServices.heading")}
          </h2>
          <p
            className="reveal reveal-delay-2 mt-4 text-base sm:text-lg text-[#FDF5EB]/65 max-w-xl leading-relaxed"
            data-testid="digital-services-sub"
          >
            {t(lang, "digitalServices.sub")}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {cards.map((c, i) => {
            const Icon = icons[i];
            return (
              <div
                key={c.title}
                data-testid={`digital-service-card-${i}`}
                className={`reveal reveal-delay-${(i % 3) + 1} feature-card relative p-7 rounded-2xl border border-white/8 bg-white/[0.02]`}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                  style={{ backgroundColor: `${accents[i]}1a` }}
                >
                  <Icon size={22} style={{ color: accents[i] }} strokeWidth={2} />
                </div>
                <h3 className="text-lg font-bold text-[#FDF5EB] mb-3 tracking-tight">
                  {c.title}
                </h3>
                <p className="text-sm text-[#FDF5EB]/60 leading-relaxed">
                  {c.desc}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Testimonial = ({ lang }) => (
  <section
    data-testid="section-quote"
    className="py-24 lg:py-32 bg-[#171717] border-y border-white/5 relative"
  >
    <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
      <svg
        className="reveal mx-auto mb-8 text-[#FBD556]"
        width="40"
        height="32"
        viewBox="0 0 40 32"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M0 32V18.6C0 8.3 5.6 1.7 16 0v6.7c-5.6 1.5-8.5 5.4-8.5 11.7H14V32H0zm24 0V18.6C24 8.3 29.6 1.7 40 0v6.7c-5.6 1.5-8.5 5.4-8.5 11.7H38V32H24z" />
      </svg>
      <blockquote
        className="reveal reveal-delay-1 text-2xl sm:text-3xl lg:text-4xl font-medium text-[#FDF5EB] leading-[1.3] tracking-tight mb-8"
        data-testid="quote-text"
      >
        "{t(lang, "testimonial.quote")} —{" "}
        <span className="text-[#FBD556]">{t(lang, "testimonial.quoteHighlight")}</span>"
      </blockquote>
      <div
        className="reveal reveal-delay-2 flex items-center justify-center gap-4"
        data-testid="quote-attrib"
      >
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FBD556] to-[#FF9456] flex items-center justify-center text-[#242220] font-bold">
          NB
        </div>
        <div className="text-left">
          <div className="text-sm font-bold text-[#FDF5EB]">
            {t(lang, "testimonial.name")}
          </div>
          <div className="text-xs text-[#FDF5EB]/55">
            {t(lang, "testimonial.role")}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Contact = ({ lang }) => (
  <section
    id="contact"
    data-testid="section-contact"
    className="py-24 lg:py-32 relative overflow-hidden"
  >
    <div className="absolute inset-0 opacity-40 pointer-events-none">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#FBD556] blur-[120px] opacity-10" />
    </div>
    <div className="max-w-3xl mx-auto px-6 md:px-10 text-center relative">
      <h2
        className="reveal text-3xl sm:text-4xl lg:text-5xl font-bold text-[#FDF5EB] tracking-tight leading-tight mb-5"
        data-testid="contact-heading"
      >
        {t(lang, "contact.heading")}
      </h2>
      <p
        className="reveal reveal-delay-1 text-base sm:text-lg text-[#FDF5EB]/65 max-w-xl mx-auto mb-12 leading-relaxed"
        data-testid="contact-subtext"
      >
        {t(lang, "contact.subtext")}
      </p>

      <div
        className="reveal reveal-delay-2 flex flex-col sm:flex-row items-center justify-center gap-4 mb-10"
        data-testid="contact-ctas"
      >
        <a
          href="mailto:info@naturtrak.eu"
          data-testid="contact-email-link"
          className="inline-flex items-center gap-2 bg-[#FBD556] text-[#242220] font-bold text-sm px-6 py-3.5 rounded-full hover:bg-[#FFE07A] transition-all hover:shadow-[0_0_30px_rgba(251,213,86,0.4)]"
        >
          <Mail size={18} strokeWidth={2.2} />
          info@naturtrak.eu
        </a>
        <a
          href="https://github.com/balakhorvathnorbert"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="contact-github-link"
          className="inline-flex items-center gap-2 text-[#FDF5EB] font-semibold text-sm px-6 py-3.5 rounded-full border border-[#FDF5EB]/20 hover:border-[#A1E1B9]/60 hover:bg-white/5 transition-all"
        >
          <Github size={18} />
          balakhorvathnorbert
        </a>
      </div>

      <a
        href="https://rust.naturtrak.eu"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="contact-api-docs"
        className="reveal reveal-delay-3 inline-flex items-center gap-2 text-sm text-[#A1E1B9] hover:text-[#FBD556] transition-colors font-medium"
      >
        {t(lang, "contact.apiDocs")}
        <ExternalLink size={14} />
      </a>
    </div>
  </section>
);

const Footer = ({ lang }) => (
  <footer
    data-testid="section-footer"
    className="bg-[#141414] border-t border-white/5 py-10"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-[#FDF5EB]/50">
      <div className="flex items-center gap-3">
        <img src={naturtrakLogo} alt="NaturTrak" className="h-6 w-auto object-contain" />
        <span>
          © 2026 NaturTrak · Aggteleki Nemzeti Park Igazgatóság
        </span>
      </div>
      <div className="flex items-center gap-4">
        <img src={termvedLogo} alt="Hungarian Nature Conservation" className="h-6 w-auto object-contain" />
        <span>{t(lang, "footer.builtIn")}</span>
        <span aria-label="Hungarian flag" role="img">🇭🇺</span>
        <span>{t(lang, "footer.forNature")}</span>
      </div>
    </div>
  </footer>
);

const Landing = () => {
  useReveal();
  const [lang, setLang] = useState("hu");

  const toggleLang = () => setLang((prev) => (prev === "en" ? "hu" : "en"));

  return (
    <div data-testid="landing-root" className="bg-[#1E1E1E] text-[#FDF5EB] min-h-screen">
      <Nav lang={lang} onToggleLang={toggleLang} />
      <main>
        <Hero lang={lang} />
        <StatsBar lang={lang} />
        <Features lang={lang} />
        <FeatureShowcase lang={lang} />
        <DeepDive lang={lang} />
        <ParksCarousel lang={lang} />
        <DigitalServices lang={lang} />
        <Ecosystem lang={lang} />
        <AthenaSection lang={lang} />
        <HowItWorks lang={lang} />
        <Testimonial lang={lang} />
        <Contact lang={lang} />
      </main>
      <Footer lang={lang} />
    </div>
  );
};

export default Landing;
